import { useEffect, useMemo, useState } from 'react';
import {
  RfUiDatepicker,
  RfUiInput,
  RfUiModal,
  RfUiSelect,
  RfUiTimeline,
} from 'renfe-components';
import {
  makeSelectJornadasUsuario,
  makeSelectPracticasByJornada,
  makeSelectPracticaSeleccionada,
} from 'store/practicas/selectors';
import { language } from 'utils/literals';
import { stateLiteral } from 'utils/statesLiterals';
import { createStructuredSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSelectedJourney,
  setStartDateTimeSelectedJourney,
  setFinishDateTimeSelectedJourney,
  setStartTimeSelectedJourneyPristine,
  setFinishTimeSelectedJourneyPristine,
  setSelectedCourse,
  setStartDateTimeSelectedPreparation,
  setStartDateTimeSelectedService,
  setFinishDateTimeSelectedService,
} from 'store/app/actions';
import { timestampToTimeString } from 'utils/dateTimeUtils';
import {
  makeSelectIsStudentProfile,
  makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected,
} from 'store/usuarios/selectors';
import { parseJourneys } from '../../utils/journeyUtils';
import {
  JornadaUsuario,
  PracticaSeleccionada,
} from '../../store/practicas/types';
import {
  makeSelectFinishDateJourneySelectedError,
  makeSelectStartJourneySelectedErrorDraft,
  makeSelectFinishTimeJourneySelectedErrorDraft,
  makeSelectSelectedJourney,
  makeSelectStartDateTimeSelectedJourney,
  makeSelectFinishDateTimeSelectedJourney,
  makeSelectStartTimeSelectedJourneyPristine,
  makeSelectFinishTimeSelectedJourneyPristine,
  makeSelectCanCreatePractice,
  makeSelectCanCreateDraft,
} from '../../store/app/selectors';
import { 
  getPracticasByJornadaModalRequest,
  resetPracticasByJornada, } from 'store/practicas/actions';
import ActionTypes from 'store/practicas/actionTypes';
import { store } from 'store/configureStore';

const selectors = createStructuredSelector({
  jornadasUsuario: makeSelectJornadasUsuario(),
  practicaSeleccionada: makeSelectPracticaSeleccionada(),
  profileActive: makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
  selectedJourney: makeSelectSelectedJourney(),
  startDateTimeSelectedJourney: makeSelectStartDateTimeSelectedJourney(),
  startTimeSelectedJourneyPristine:
    makeSelectStartTimeSelectedJourneyPristine(),
  finishDateTimeSelectedJourney: makeSelectFinishDateTimeSelectedJourney(),
  finishTimeSelectedJourneyPristine:
    makeSelectFinishTimeSelectedJourneyPristine(),
  startJourneySelectedErrorDraft: makeSelectStartJourneySelectedErrorDraft(),
  finishDateJourneySelectedError: makeSelectFinishDateJourneySelectedError(),
  finishTimeJourneySelectedErrorDraft:
    makeSelectFinishTimeJourneySelectedErrorDraft(),
  canCreatePractice: makeSelectCanCreatePractice(),
  canCreateDraft: makeSelectCanCreateDraft(),
  isStudentProfile: makeSelectIsStudentProfile(),
  practicasJornadaList: makeSelectPracticasByJornada(),
} as any);

const JourneysContainer = () => {
  const dispatch = useDispatch();
  const {
    jornadasUsuario,
    practicaSeleccionada,
    selectedJourney,
    startDateTimeSelectedJourney,
    startTimeSelectedJourneyPristine,
    finishDateTimeSelectedJourney,
    finishTimeSelectedJourneyPristine,
    startJourneySelectedErrorDraft,
    finishDateJourneySelectedError,
    finishTimeJourneySelectedErrorDraft,
    isStudentProfile,
    practicasJornadaList,
  } = useSelector<any, any>(selectors);

  const editMode: boolean = useMemo(
    () => !!practicaSeleccionada.CUERPO_FICHA,
    [practicaSeleccionada]
  );

  const calculatedJourneys = useMemo(() => {
    if (jornadasUsuario?.length) {
      const options = parseJourneys(jornadasUsuario).map((option) => {
        return {
          ...option,
          active:
            option.value.ID_JORNADA === selectedJourney?.ID_JORNADA
              ? 'true'
              : 'false',
        };
      });
      return JSON.stringify(options);
    }
    return '[]';
  }, [jornadasUsuario, selectedJourney]);

  const [modalType, setModalType] = useState('');
  const [modalJourneysJSON, setModalJourneysJSON] = useState(
    JSON.stringify(practicasJornadaList)
  );

  useEffect(() => {
    setModalJourneysJSON(JSON.stringify(practicasJornadaList));
  }, [practicasJornadaList]); // Se ejecuta cada vez que practicasJornadaList cambie

  const calculatedStartDateSelectedJourney: string | undefined = useMemo(() => {
    if (startDateTimeSelectedJourney) {
      const date = new Date(startDateTimeSelectedJourney);
      date.setHours(0, 0, 0, 0);
      return date.getTime().toString();
    }
  }, [startDateTimeSelectedJourney]);

  const calculatedStartTimeSelectedJourney: string = useMemo(() => {
    if (startTimeSelectedJourneyPristine) {
      return '--:--';
    }
    return timestampToTimeString(startDateTimeSelectedJourney);
  }, [startDateTimeSelectedJourney, startTimeSelectedJourneyPristine]);

  const calculatedFinishDateSelectedJourney: string = useMemo(() => {
    return finishDateTimeSelectedJourney
      ? finishDateTimeSelectedJourney.getTime().toString()
      : null;
  }, [finishDateTimeSelectedJourney]);

  const calculatedFinishTimeSelectedJourney: string = useMemo(() => {
    if (finishTimeSelectedJourneyPristine) {
      return '--:--';
    }
    return timestampToTimeString(finishDateTimeSelectedJourney);
  }, [finishDateTimeSelectedJourney, finishTimeSelectedJourneyPristine]);

  // Condiciones de edición

  const canEditFinishDateSelectedJourney: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;

    let todasEnEstadoValido;
    if(practicasJornadaList.length > 0){
       todasEnEstadoValido = practicasJornadaList.every(practica => 
            practica.ID_EST_PRACTICA === stateLiteral.borrador 
          || practica.ID_EST_PRACTICA === stateLiteral.pendCorrecion
            );
      }else if(practicasJornadaList){
           todasEnEstadoValido = true;
      }
      else {
          todasEnEstadoValido = false;
      }

    if(estadoPractica === null){
      return (
        isStudentProfile &&
        todasEnEstadoValido
      );
    }else{
      return (
        isStudentProfile &&
        !!estadoPractica &&
        todasEnEstadoValido &&
        [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
          estadoPractica
        ) > -1
      );
  }
  }, [isStudentProfile, practicaSeleccionada, practicasJornadaList]);

const canEditFinishTimeSelectedJourney: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;

    let todasEnEstadoValido;
    if(practicasJornadaList.length > 0){
       todasEnEstadoValido = practicasJornadaList.every(practica => 
            practica.ID_EST_PRACTICA === stateLiteral.borrador 
          || practica.ID_EST_PRACTICA === stateLiteral.pendCorrecion
            );
      }else if(practicasJornadaList){
           todasEnEstadoValido = true;
      }
      else {
          todasEnEstadoValido = false;
      }
      
      if(estadoPractica === null){
      return (
        isStudentProfile &&
        todasEnEstadoValido
      );
    }else{
        return (
          isStudentProfile &&
          !!estadoPractica &&
          todasEnEstadoValido &&
          [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
            estadoPractica
          ) > -1
    );
  } 
  }, [isStudentProfile, practicaSeleccionada, practicasJornadaList]);


  // Eventos

  const handleChangeSelectedJourney = (e: any) => {
    const journeySelected: JornadaUsuario = e.detail?.value ?? null;
    console.log("Journey Selected:", journeySelected); // Verifica el contenido
    dispatch(setSelectedJourney(journeySelected));
    let newStartDateTimeSelectedJourney: Date;
    let newFinishDateTimeSelectedJourney: Date;
    if (journeySelected) {
      newStartDateTimeSelectedJourney = new Date(
        journeySelected.FECHA_INI_JORNADA
      );
      newFinishDateTimeSelectedJourney = new Date(
        journeySelected.FECHA_FIN_JORNADA
      );
      dispatch(setStartTimeSelectedJourneyPristine(false));
      dispatch(setFinishTimeSelectedJourneyPristine(false));
      dispatch(setSelectedCourse(journeySelected.ID_CURSO.toString()));
      dispatch(getPracticasByJornadaModalRequest());
    } else {
      newStartDateTimeSelectedJourney = new Date();
      newStartDateTimeSelectedJourney.setHours(0, 0, 0, 0);
      newFinishDateTimeSelectedJourney = new Date();
      newFinishDateTimeSelectedJourney.setHours(0, 0, 0, 0);
      dispatch(setStartTimeSelectedJourneyPristine(true));
      dispatch(setFinishTimeSelectedJourneyPristine(true));
      dispatch(setSelectedCourse(''));
      dispatch(resetPracticasByJornada());
    }
    dispatch(setStartDateTimeSelectedJourney(newStartDateTimeSelectedJourney));
    dispatch(
      setFinishDateTimeSelectedJourney(newFinishDateTimeSelectedJourney)
    );
  };

  const openModal = (typeModal: string) => {
    setModalType(typeModal);
  };

  const closeModal = () => {
    setModalType('');
    setModalJourneysJSON('');
  };

  const handleJourneyTooltipClick = () => {
    dispatch(getPracticasByJornadaModalRequest());
    setModalJourneysJSON(JSON.stringify(practicasJornadaList));
    openModal('info');
  };

  const handleJourneyTooltipClickManual = () => {
    setModalJourneysJSON(JSON.stringify(practicasJornadaList));
    openModal('info');
  };

  const getPracticasJornada = async () => {
    const journeySelected = {ID_JORNADA: (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA?.ID_JORNADA};
  
    dispatch(setSelectedJourney((journeySelected as JornadaUsuario)));
    dispatch(setSelectedCourse((practicaSeleccionada as PracticaSeleccionada)?.CABECERA_FICHA?.ID_CURSO.toString()));
  
    try {
      // Esperamos a que el dispatch termine usando una promesa
      await new Promise((resolve, reject) => {
        dispatch({
          type: ActionTypes.GET_PRACTICAS_BY_JORNADA_REQUEST,
          payload: { resolve, reject },
        });
      });
      
      handleJourneyTooltipClickManual(); // Solo se ejecuta después de que todo termine
    } catch (error) {
      console.error("Error al obtener las prácticas:", error);
    }
  };


  const handleChangeStartDateSelectedJourney = (e: CustomEvent) => {
    const newDate = startDateTimeSelectedJourney
      ? new Date(startDateTimeSelectedJourney)
      : new Date();
    const inputDate = new Date(e.detail);
    newDate.setFullYear(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate()
    );
    dispatch(setStartDateTimeSelectedJourney(newDate));
    dispatch(setStartDateTimeSelectedPreparation(newDate));
    dispatch(setStartDateTimeSelectedService(newDate));
  };

  const handleChangeStartTimeSelectedJourney = (e: CustomEvent) => {
    const newDate = startDateTimeSelectedJourney
      ? new Date(startDateTimeSelectedJourney)
      : new Date();
    const data = e.detail ? e.detail.split(':') : null;
    if (data?.length === 2) {
      dispatch(setStartTimeSelectedJourneyPristine(false));
      const [hoursStr, minutesStr] = data;
      newDate.setHours(parseInt(hoursStr), parseInt(minutesStr), 0, 0);
    } else {
      dispatch(setStartTimeSelectedJourneyPristine(true));
      newDate.setHours(0, 0, 0, 0);
    }
    dispatch(setStartDateTimeSelectedJourney(newDate));
  };

  const handleChangeFinishDateSelectedJourney = (e: CustomEvent) => {
    const newDate = finishDateTimeSelectedJourney
      ? new Date(finishDateTimeSelectedJourney)
      : new Date();
    const inputDate = new Date(e.detail);
    newDate.setFullYear(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate()
    );
    dispatch(setFinishDateTimeSelectedJourney(newDate));
    //si la jornada no tiene practicas estamos creando una jornada nueva y
    //se debe permitir que la practica que se esta creando se 
    //actualice acorde al final de la jornada a la que pertenece
    if(practicasJornadaList.length === 0){
      dispatch(setFinishDateTimeSelectedService(newDate));
    }  
  };

  const handleChangeFinishTimeSelectedJourney = (e: CustomEvent) => {
    const newDate = finishDateTimeSelectedJourney
      ? new Date(finishDateTimeSelectedJourney)
      : new Date();
    const data = e.detail ? e.detail.split(':') : null;
    if (data?.length === 2) {
      dispatch(setFinishTimeSelectedJourneyPristine(false));
      const [hoursStr, minutesStr] = data;
      newDate.setHours(parseInt(hoursStr), parseInt(minutesStr), 0, 0);
    } else {
      dispatch(setFinishTimeSelectedJourneyPristine(true));
      newDate.setHours(0, 0, 0, 0);
    }
    dispatch(setFinishDateTimeSelectedJourney(newDate));
  };

  return (
    <>
      {editMode ? (
        // EDIT MODE
        <div>
          <div className="rf-practice-detail__info">
            <h2 className="rf-title rf-title--secundary">
              {language.informacionPractica.jornadaLabel}
            </h2>

            <div>
              <button
                    type="button"
                    onClick={() => getPracticasJornada()}
                    aria-label="Mostrar prácticas de la jornada"
                    ><i className="icon-info"></i>
              </button>
            </div>
          </div>

          <div>
            <div className="rf-box__child">
              <div className="rf-practice-detail__input-container rf-practice-list__input-container--pr">
                <RfUiDatepicker
                  required={'black'}
                  position={'left'}
                  moment-locale="es"
                  labelInput={language.informacionPractica.dateDesdeLabel}
                  disabled={true}
                  text-apply-button={language.informacionPractica.aplicarLabel}
                  onDateSelected={handleChangeStartDateSelectedJourney}
                  defaultDateFrom={calculatedStartDateSelectedJourney}
                />
              </div>
            </div>
            <div className="rf-box__child">
              <div className="rf-practice-detail__input-container">
                <RfUiDatepicker
                  required={'black'}
                  moment-locale="es"
                  position={'left'}
                  labelInput={language.informacionPractica.dateHastaLabel}
                  disabled={!canEditFinishDateSelectedJourney}
                  text-apply-button={language.informacionPractica.aplicarLabel}
                  onDateSelected={handleChangeFinishDateSelectedJourney}
                  defaultDateFrom={calculatedFinishDateSelectedJourney}
                  defaultMinDateFrom={calculatedStartDateSelectedJourney}
                  textError={finishDateJourneySelectedError}
                />
              </div>
            </div>
            
            <div className="rf-box__child">
              <div className="rf-practice-detail__input-container rf-practice-list__input-container--pr">
                <RfUiInput
                  required={'black'}
                  label={language.informacionPractica.hourInicioLabel}
                  error=""
                  className="rf-input"
                  typeInput="time"
                  disabled={true}
                  onValueChanges={handleChangeStartTimeSelectedJourney}
                  value={calculatedStartTimeSelectedJourney}
                  textError={startJourneySelectedErrorDraft}
                />
              </div>
            </div>
            <div className="rf-box__child">
              <div className="rf-practice-detail__input-container">
                <RfUiInput
                  required={'black'}
                  label={language.informacionPractica.hourFinLabel}
                  disabled={!canEditFinishTimeSelectedJourney}
                  error=""
                  typeInput="time"
                  className="rf-input"
                  onValueChanges={handleChangeFinishTimeSelectedJourney}
                  value={calculatedFinishTimeSelectedJourney}
                  textError={finishTimeJourneySelectedErrorDraft}
                />
              </div>
            </div>
          </div>
          <RfUiModal
            title-modal="Listado de prácticas por jornada"
            reference-click="modal-journey-timeline"
            slot-content="true"
            id-modal="modalJourney"
            modal-type={modalType}
            content-border="false"
            allow-scroll="true"
            onModalClose={(e: any) => {
              closeModal();
            }}
          >
            <div slot="content">
              <RfUiTimeline
                journeyData={selectedJourney?.ID_JORNADA}
                practices-data={modalJourneysJSON}
              ></RfUiTimeline>
            </div>
          </RfUiModal>
        </div>
      ) : (
        //Crear práctica
        <div>
          <h2 className="rf-title rf-title--secundary">
            {language.informacionPractica.jornadaLabel}
          </h2>
          <div className="rf-practice-create__input-container rf-practice-create__input-container-journey">
            <RfUiSelect
              label="Jornada"
              options={calculatedJourneys}
              onOptionActive={(e) => handleChangeSelectedJourney(e)}
              addEmptyOption={true}
              placeHolder={language.informacionPractica.jornadaPlaceholder}
              /* fieldId="ID_JORNADA" */
              isJornadaSelector={true}
            />
            {selectedJourney && (
            <button
              type="button"
              onClick={() => handleJourneyTooltipClick()}
              aria-label="Mostrar prácticas de la jornada"
            ><i className="icon-info"></i>
            </button>
            )}
          </div>
          <div className="rf-box__child">
            <div className="rf-practice-create__input-container rf-practice-list__input-container--pr">
              <RfUiDatepicker
                required={'black'}
                moment-locale="es"
                position={'left'}
                labelInput={language.informacionPractica.dateDesdeLabel}
                text-apply-button={language.informacionPractica.aplicarLabel}
                onDateSelected={handleChangeStartDateSelectedJourney}
                defaultDateFrom={calculatedStartDateSelectedJourney}
                disabled={!!selectedJourney}
                placeholder={
                  language.informacionPractica.hourInicioPlaceholderLabel
                }
              />
            </div>
          </div>
          <div className="rf-box__child">
            <div className="rf-practice-create__input-container">
              <RfUiDatepicker
                required={'black'}
                moment-locale="es"
                position={'left'}
                labelInput={language.informacionPractica.dateHastaLabel}
                text-apply-button={language.informacionPractica.aplicarLabel}
                onDateSelected={handleChangeFinishDateSelectedJourney}
                disabled={!canEditFinishDateSelectedJourney}
                placeholder={
                  language.informacionPractica.hourFinPlaceholderLabel
                }
                textError={finishDateJourneySelectedError}
                defaultDateFrom={calculatedFinishDateSelectedJourney}
              />
            </div>
          </div>
          <div className="rf-box__child">
            <div className="rf-practice-create__input-container rf-practice-list__input-container--pr">
              <RfUiInput
                required={'black'}
                label={language.informacionPractica.hourInicioLabel}
                error=""
                typeInput="time"
                className="rf-input"
                onValueChanges={handleChangeStartTimeSelectedJourney}
                textError={startJourneySelectedErrorDraft}
                disabled={!!selectedJourney}
                placeholder="XX:XX"
                value={calculatedStartTimeSelectedJourney}
              />
            </div>
          </div>
          <div className="rf-box__child">
            <div className="rf-practice-create__input-container">
              <RfUiInput
                required={'black'}
                label={language.informacionPractica.hourFinLabel}
                error=""
                typeInput="time"
                className="rf-input"
                onValueChanges={handleChangeFinishTimeSelectedJourney}
                disabled={!canEditFinishTimeSelectedJourney}
                placeholder="XX:XX"
                textError={finishTimeJourneySelectedErrorDraft}
                value={calculatedFinishTimeSelectedJourney}
              />
            </div>
          </div>
          <RfUiModal
            title-modal="Listado de prácticas por jornada"
            reference-click="modal-journey-timeline"
            slot-content="true"
            id-modal="modalJourney"
            modal-type={modalType}
            content-border="false"
            allow-scroll="true"
            onModalClose={(e: any) => {
              closeModal();
            }}
          >
            <div slot="content">
              <RfUiTimeline
                journeyData={selectedJourney?.ID_JORNADA}
                practices-data={modalJourneysJSON}
              ></RfUiTimeline>
            </div>
          </RfUiModal>
        </div>
      )}
    </>
  );
};

export default JourneysContainer;
